import { useRef, type ClipboardEvent, type KeyboardEvent } from 'react';
import { cn } from '@/utils/cn';

interface PinInputProps {
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  className?: string;
  size?: 'default' | 'lg';
}

export function PinInput({
  value,
  onChange,
  disabled,
  autoFocus,
  className,
  size = 'default',
}: PinInputProps) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const isLarge = size === 'lg';

  function focusBox(index: number, select = false) {
    const input = refs.current[index];
    if (!input) return;
    input.focus();
    if (select) {
      requestAnimationFrame(() => input.select());
    }
  }

  function updateDigit(index: number, digit: string) {
    if (!/^\d?$/.test(digit)) return;
    const next = [...value];
    next[index] = digit;
    onChange(next);
    if (digit && index < 3) focusBox(index + 1);
  }

  function clearDigit(index: number) {
    if (!value[index]) return false;
    const next = [...value];
    next[index] = '';
    onChange(next);
    return true;
  }

  function handleBackspace(index: number) {
    if (clearDigit(index)) {
      focusBox(index);
      return;
    }

    if (index > 0) {
      const next = [...value];
      next[index - 1] = '';
      onChange(next);
      focusBox(index - 1, true);
    }
  }

  function handleKeyDown(index: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' || e.key === 'Delete') {
      e.preventDefault();
      handleBackspace(index);
      return;
    }

    if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      focusBox(index - 1, true);
      return;
    }

    if (e.key === 'ArrowRight' && index < 3) {
      e.preventDefault();
      focusBox(index + 1, true);
    }
  }

  function handlePaste(index: number, e: ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '');
    if (!pasted) return;

    const next = [...value];
    for (let i = 0; i < pasted.length && index + i < 4; i += 1) {
      next[index + i] = pasted[i]!;
    }
    onChange(next);

    const nextFocus = Math.min(index + pasted.length, 3);
    focusBox(nextFocus, !next[nextFocus]);
  }

  return (
    <div className={cn('flex justify-center gap-2.5 sm:gap-3', className)}>
      {value.map((digit, index) => (
        <input
          key={index}
          ref={(el) => {
            refs.current[index] = el;
          }}
          type="password"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          maxLength={1}
          value={digit}
          disabled={disabled}
          autoFocus={autoFocus && index === 0}
          aria-label={`PIN digit ${index + 1}`}
          className={cn(
            'rounded-xl border-2 text-center font-bold text-brand-navy transition-all',
            'focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/15',
            isLarge ? 'h-12 w-10 text-lg sm:h-14 sm:w-11' : 'h-11 w-9 text-base shadow-sm sm:h-12 sm:w-10',
            digit ? 'border-primary/40 bg-primary/5' : 'border-gray-200 bg-white',
            disabled && 'opacity-60',
          )}
          onChange={(e) => updateDigit(index, e.target.value.replace(/\D/g, ''))}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={(e) => handlePaste(index, e)}
          onFocus={(e) => e.currentTarget.select()}
        />
      ))}
    </div>
  );
}

export function pinArrayToString(digits: string[]) {
  return digits.join('');
}

export function emptyPin() {
  return ['', '', '', ''];
}
